import { useState } from 'react';
import { ErrorMessage, Field, Formik, Form } from 'formik';
import * as Yup from 'yup';
import {
  Button,
  IconButton,
  InputAdornment,
  TextField,
  Modal,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useDispatch, useSelector } from 'react-redux';
import { changePassword } from '../../features/auth/authSlice';
import { logout } from '../../utils/logout';
import { useNavigate } from 'react-router-dom';

const PasswordField = ({
  label,
  name,
  showPassword,
  setShowPassword,
  placeholder,
}) => (
  <>
    <label className={name === 'confirmPassword' ? 'mt-3 mb-1' : ''}>
      {label}
    </label>
    <Field
      as={TextField}
      name={name}
      placeholder={placeholder}
      type={showPassword ? 'text' : 'password'}
      size='small'
      autoComplete={name === 'newPassword' ? 'new-password' : undefined}
      variant='outlined'
      fullWidth
      InputProps={{
        className: 'bg-white',
        endAdornment: (
          <InputAdornment position='end'>
            <IconButton
              onClick={() => setShowPassword((prev) => !prev)}
              edge='end'
              size='small'
            >
              {showPassword ? <Visibility /> : <VisibilityOff />}
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
    <ErrorMessage component='div' name={name} className='text-red-600' />
  </>
);

const SuccessModal = ({ open }) => (
  <Modal
    open={open}
    aria-labelledby='modal-modal-title'
    aria-describedby='modal-modal-description'
  >
    <div className='bg-white fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 p-8 rounded-3xl text-center w-[90%] max-w-md shadow-lg'>
      <img
        src='/images/success.gif'
        alt='Success'
        className='mx-auto mb-4 size-40 rounded-full object-cover'
      />
      <h2
        id='modal-modal-title'
        className='text-2xl font-semibold mb-2 text-green-600'
      >
        Password Updated!
      </h2>
      <p id='modal-modal-description' className='text-gray-600 mb-6'>
        Your password has been successfully changed. You will be redirected to
        login.
      </p>
    </div>
  </Modal>
);

const validationSchema = Yup.object({
  newPassword: Yup.string().required('New password is required'),
  confirmPassword: Yup.string()
    .required('Confirm password is required')
    .oneOf([Yup.ref('newPassword')], 'Passwords must match'),
});

const ChangePassword = () => {
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [successModal, setSuccessModal] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { userDetails } = useSelector((state) => state.auth.successData);

  const handleSubmit = async (values, { resetForm }) => {
    try {
      const response = await dispatch(
        changePassword({
          email: userDetails?.email,
          newPassword: values.confirmPassword,
        }),
      ).unwrap();

      if (response) {
        setSuccessModal(true);
        resetForm();
        setTimeout(async () => {
          setSuccessModal(false);
          await logout();
          navigate('/signin');
        }, 2000);
      } else {
        alert('Error in Updating the Password');
      }
    } catch {
      alert('Error in Updating the Password');
    }
  };

  return (
    <div className='bg-[#eef0f7] p-4 mt-3 rounded-lg'>
      <h5 className='text-blue-700 font-bold font-sans'>Change Password</h5>
      <Formik
        initialValues={{ newPassword: '', confirmPassword: '' }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isValid, dirty }) => (
          <Form>
            <div className='flex flex-col mt-3 max-w-xs'>
              <PasswordField
                label='New Password'
                name='newPassword'
                showPassword={showNewPassword}
                setShowPassword={setShowNewPassword}
                placeholder='Enter New Password'
              />
              <PasswordField
                label='Confirm Password'
                name='confirmPassword'
                showPassword={showConfirmPassword}
                setShowPassword={setShowConfirmPassword}
                placeholder='Enter Confirm New Password'
              />
              <Button
                type='submit'
                variant='contained'
                color='primary'
                disabled={!(isValid && dirty)}
                className='w-20 py-2 ml-auto mt-3'
              >
                Save
              </Button>
            </div>
          </Form>
        )}
      </Formik>
      <SuccessModal open={successModal} />
    </div>
  );
};

export default ChangePassword;
